import { Matrix, Resource, TextStyle, Texture } from "pixi.js";
import { Container, Graphics, Sprite, Text } from "@pixi/react";
import EvStationSvg from "./Sprite/electricCharging.svg";
import { ParkPlaceIcon } from "./Sprite/SpriteIndex";
import BoundingBox from "../../Domain/Types/BoundingBox.type";

/** Occupancy-circle-relative size; slightly inset so P / charger sit inside the white ring. */
const PARK_ICON_WIDTH_FACTOR = 1.15;
const PARK_ICON_HEIGHT_FACTOR = 1.8;
const CHARGING_ICON_SCALE_FACTOR = 0.0045;

export const GeneParkingPlaceIcon = ({
  useParkIconText,
  isSelectable,
  circleRadius,
  placeScale,
  placeTypeId,
  boundingBox
}: {
  useParkIconText: boolean;
  isSelectable: boolean;
  circleRadius: number;
  placeScale: number;
  placeTypeId: number;
  boundingBox: BoundingBox;
}) => {
  const circleCenter = {
    x: 0,
    y: 0 - (boundingBox.height * placeScale) / 4
  };

  // electric charging place
  if (placeTypeId === 4) {
    return (
      <Sprite
        data-testid="place-icon-electric-charging"
        image={EvStationSvg}
        scale={circleRadius * CHARGING_ICON_SCALE_FACTOR}
        anchor={0.5}
        x={circleCenter.x}
        y={circleCenter.y}
        cursor={isSelectable ? "pointer" : "auto"}
      />
    );
  }

  // parking place // if (placeTypeId === 2) {
  else {
    // because of the performance issue, not use the Text object
    if (useParkIconText) {
      return (
        <Text
          data-testid="place-icon-text-parkingplace"
          text={"P"}
          style={new TextStyle({ fill: 0xffffff })}
          resolution={3}
          cursor={isSelectable ? "pointer" : "auto"}
          width={circleRadius * PARK_ICON_WIDTH_FACTOR}
          height={circleRadius * PARK_ICON_HEIGHT_FACTOR}
          anchor={0.5}
          x={circleCenter.x}
          y={circleCenter.y}
        />
      );
    }

    // because of the performance issue, use the Sprite object
    return (
      <Sprite
        data-testid="place-icon-spr-parkingplace"
        image={ParkPlaceIcon}
        cursor={isSelectable ? "pointer" : "auto"}
        width={circleRadius * PARK_ICON_WIDTH_FACTOR}
        height={circleRadius * PARK_ICON_HEIGHT_FACTOR}
        anchor={0.5}
        x={circleCenter.x}
        y={circleCenter.y}
      />
    );
  }
};

/** Photo sits inside the occupancy circle so red/grey status stays visible. */
export const PROFILE_PHOTO_RADIUS_RATIO = 0.62;

export function profilePhotoRadius(statusCircleRadius: number): number {
  return statusCircleRadius * PROFILE_PHOTO_RADIUS_RATIO;
}

type ProfilePhotoGraphics = {
  clear: () => void;
  beginFill?: (color: number) => void;
  beginTextureFill: (opts: { texture: Texture<Resource>; matrix: Matrix }) => void;
  drawCircle: (x: number, y: number, radius: number) => void;
  endFill: () => void;
};

export function drawOccupancyProfilePhoto(
  g: ProfilePhotoGraphics,
  iconImage: Texture<Resource>,
  statusCircleRadius: number,
  rotation: number,
  inset: boolean
) {
  const textureWidth = iconImage.width;
  const textureHeight = iconImage.height;
  if (!textureWidth || !textureHeight) {
    g.clear();
    return;
  }

  const photoRadius = inset ? profilePhotoRadius(statusCircleRadius) : statusCircleRadius;
  const diameter = photoRadius * 2;
  const mtx = new Matrix();
  mtx.scale(diameter / textureWidth, diameter / textureHeight);
  mtx.translate(-photoRadius, -photoRadius);
  mtx.rotate(-(rotation * Math.PI) / 180);

  g.clear();
  if (!inset) {
    g.beginFill?.(0xffffff);
    g.drawCircle(0, 0, statusCircleRadius);
    g.endFill();
  }
  g.beginTextureFill({ texture: iconImage, matrix: mtx });
  g.drawCircle(0, 0, photoRadius);
  g.endFill();
}

export const GeneIconImageGrp = ({
  iconImage,
  circleRadius,
  rotation,
  placeTypeId,
  placeScale,
  boundingBox,
  isSelectable,
  insetProfilePhoto = false
}: {
  iconImage: Texture<Resource>;
  circleRadius: number;
  rotation: number;
  placeTypeId: number;
  placeScale: number;
  boundingBox: BoundingBox;
  isSelectable: boolean;
  /** True in booking select (`SELECT.BOOK`); display keeps the full occupancy circle. */
  insetProfilePhoto?: boolean;
}) => {
  return (
    <Graphics
      data-testid="place-icon-grp-user-img"
      draw={g => drawOccupancyProfilePhoto(g, iconImage, circleRadius, rotation, insetProfilePhoto)}
      x={0}
      y={placeTypeId === 1 ? 0 - 16 * placeScale : 0 - (boundingBox.height * placeScale) / 4}
      cursor={isSelectable ? "pointer" : "auto"}
    />
  );
};

/** Occupant initials sit on the occupancy circle (usually red), so they stay white for own and colleague desks. */
export const OCCUPANCY_INITIAL_FILL = 0xffffff;

const getTextStyle = (fontSize?: number) =>
  new TextStyle({
    fill: OCCUPANCY_INITIAL_FILL,
    align: "center",
    ...(fontSize && { fontSize })
  });

const getCommonTextProps = (
  isSelectable: boolean,
  circleRadius: number,
  isColleaguesPlace?: boolean,
  uprightRotationRad?: number
) => ({
  resolution: isColleaguesPlace ? 10 : 20,
  cursor: isSelectable ? "pointer" : "auto",
  width: circleRadius,
  height: circleRadius,
  anchor: 0.5,
  ...(uprightRotationRad != null ? { rotation: uprightRotationRad } : {})
});

export const InitialText = ({
  text,
  style,
  rotation,
  ...props
}: {
  text: string;
  style: TextStyle;
  rotation?: number;
  [key: string]: any;
}) => <Text text={text} style={style} rotation={rotation} {...props} />;

/** Vertical gap from the occupancy-circle centre to stacked initials / +N. */
export function extraOccupantLineOffset(circleRadius: number): number {
  return circleRadius * 0.38;
}

/** Shifts the stacked initials / +N down slightly so they sit optically in the circle. */
export function extraOccupantStackDownOffset(circleRadius: number): number {
  return circleRadius * 0.14;
}

/**
 * Screen-down offset of the stacked labels, in the place's local coordinates
 * (parent PlaceContainer is rotated with the desk).
 */
export function extraOccupantStackPosition(
  posY: number,
  circleRadius: number,
  rotationDegrees: number
): { x: number; y: number } {
  const down = extraOccupantStackDownOffset(circleRadius);
  const rad = (rotationDegrees * Math.PI) / 180;
  return {
    x: down * Math.sin(rad),
    y: posY + down * Math.cos(rad)
  };
}

export const WPInitialText = ({
  icon,
  circleRadius,
  posY,
  rotation,
  isColleaguesPlace,
  isSelectable
}: {
  icon: string;
  circleRadius: number;
  posY: number;
  rotation: number;
  isColleaguesPlace: boolean | undefined;
  isSelectable: boolean;
}) => {
  const uprightRotationRad = -rotation * (Math.PI / 180);

  // if icon string shows the initial only
  if (icon.length === 2) {
    return (
      <InitialText
        data-testid="place-icon-text-initial-users"
        text={icon.toUpperCase()}
        x={0}
        y={posY}
        style={getTextStyle(circleRadius * 2)}
        {...getCommonTextProps(
          isSelectable,
          circleRadius * 1.3,
          isColleaguesPlace,
          uprightRotationRad
        )}
      />
    );
  }

  // if icon string shows has number of other booking 'FR+2'
  if (icon.length >= 4 && icon.length <= 6) {
    const lineOffset = extraOccupantLineOffset(circleRadius);
    const stackPos = extraOccupantStackPosition(posY, circleRadius, rotation);
    return (
      <Container
        data-testid="place-icon-text-initial-stack"
        x={stackPos.x}
        y={stackPos.y}
        angle={-rotation}
      >
        <InitialText
          data-testid="place-icon-text-initial-users-upper"
          text={icon.substring(0, 2).toUpperCase()}
          x={0}
          y={-lineOffset}
          style={getTextStyle(circleRadius * 1.1)}
          {...getCommonTextProps(isSelectable, circleRadius * 1.1, isColleaguesPlace)}
        />
        <InitialText
          data-testid="place-icon-text-initial-users-bottom"
          text={icon.substring(2).toUpperCase()}
          x={0}
          y={lineOffset}
          style={getTextStyle(circleRadius * 0.8)}
          {...getCommonTextProps(isSelectable, circleRadius * 0.8, isColleaguesPlace)}
        />
      </Container>
    );
  }
};
